/**
 * Content Layout
 * Layout for content module with collection navigation sidebar
 */

import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router';
import { useEffect, useMemo } from 'react';
import { useCollections, type Collection } from '@/hooks/useCollections';
import { CollectionNavigation } from '@/components/content/CollectionNavigation';
import { Loader2 } from 'lucide-react';

export function ContentLayout() {
    const { collection } = useParams<{ collection?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { data: collections = [], isLoading: loading } = useCollections();

    // Get active bookmark ID from URL
    const activeBookmarkId = useMemo(() => {
        const bookmarkParam = searchParams.get('bookmark');
        return bookmarkParam ? parseInt(bookmarkParam, 10) : null;
    }, [searchParams]);

    // Filter to only user collections (not system collections)
    const userCollections = collections.filter((c: Collection) => !c.collection.startsWith('cms_'));

    // Auto-navigate to first collection if no collection is selected
    useEffect(() => {
        if (!loading && !collection && userCollections.length > 0) {
            // Check localStorage for last accessed collection
            const lastCollection = localStorage.getItem('last-accessed-collection');
            if (lastCollection && userCollections.find((c: Collection) => c.collection === lastCollection)) {
                navigate(`/admin/content/${lastCollection}`, { replace: true });
            } else if (userCollections[0]) {
                navigate(`/admin/content/${userCollections[0].collection}`, { replace: true });
            }
        }
    }, [loading, collection, userCollections, navigate]);

    // Save last accessed collection
    useEffect(() => {
        if (collection) {
            localStorage.setItem('last-accessed-collection', collection);
        }
    }, [collection]);

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="flex h-full">
            {/* Collection Navigation Sidebar */}
            <CollectionNavigation
                collections={userCollections}
                currentCollection={collection}
                activeBookmarkId={activeBookmarkId}
            />

            {/* Main Content Area - Each page handles its own right sidebar */}
            <div className="w-full overflow-hidden">
                <Outlet />
            </div>
        </div>
    );
}
